import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPodData } from 'services/pod/types';
import { RootState } from 'store/index';
import {
  getFreshServiceDataByContactId,
  getFreshServiceDataByTicketId,
  ThunkGetAutotaskPodData,
  ThunkGetAutotaskPodDataByContactId,
  ThunkGetConnectwisePodData,
  ThunkGetConnectwisePodDataByContactId,
  ThunkGetConnectwisePodDataByProjectTicketId,
  ThunkGetHaloPodByContactId,
  ThunkGetHaloPodData,
  ThunkGetImportIntegrationPodDataByContactId,
  ThunkGetKaseyaPodDataByContactId,
  ThunkGetCommonPodDataByTicketId,
  ThunkGetMicrosoftPodDataByContactId,
  ThunkGetSuperOpsDataByContactId,
  ThunkGetServiceNowDataByContactId,
  ThunkGetSuperOpsDataByTicketId,
  ThunkGetSyncroPodDataByContactId,
  ThunkGetSyncroPodDataByTicketId,
  ThunkGetZendeskPodDataByContactId,
  ThunkGetZendeskPodDataByTicketId,
  ThunkUpdatePsaContact,
  ThunkGetCommonPodDataByContactId,
} from 'store/pod/thunks';

type IInitialState = Partial<IPodData>;

export interface IVerificationMessage {
  CrmId: number;
  ContactId: string;
  Type: string;
  Value: string;
  ValidationType: string;
  InspectorName: string;
  Note?: string;
}

const initialState: IInitialState = {};

const phoneIncludes = (statePhone: string, inboundPhone: string): boolean => {
  let statePhoneDigits = statePhone.replace(/\D+/g, '');
  if (statePhoneDigits.charAt(0) === '0') {
    statePhoneDigits = statePhoneDigits.slice(1);
  }
  const inboundPhoneDigits = inboundPhone.replace(/\D+/g, '');
  return inboundPhoneDigits.includes(statePhoneDigits);
};

const ticketsSlice = createSlice({
  name: 'pod',
  reducers: {
    updateCommunicationItems: (state, action: PayloadAction<IVerificationMessage>) => {
      return {
        ...state,
        isVerified: true,
        history: state.history && [
          {
            Date: new Date().toISOString(),
            Item: action.payload.Value,
            Type: action.payload.ValidationType,
            Inspector: action.payload.InspectorName,
            Notes: action.payload.Note,
            IsRevoked: false,
          },
          ...state.history,
        ],
        communicationItems:
          state?.communicationItems &&
          state?.communicationItems.map(i => {
            const itemType = i?.Type || i?.Type;
            if (itemType !== action.payload.Type) return i;
            const itemValue = i.Value;
            if (action.payload.Type === 'email' && itemValue === action.payload.Value) {
              return {
                ...i,
                IsVerified: true,
              };
            }
            if (action.payload.Type === 'phone' && phoneIncludes(itemValue, action.payload.Value)) {
              return {
                ...i,
                IsVerified: true,
              };
            }

            return i;
          }),
      };
    },
  },
  initialState,
  extraReducers: ({ addCase }) => {
    addCase(ThunkGetConnectwisePodData.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetConnectwisePodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(
      ThunkGetConnectwisePodDataByProjectTicketId.fulfilled,
      (state, action) => action.payload,
    );
    addCase(ThunkGetAutotaskPodData.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetAutotaskPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetHaloPodData.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetHaloPodByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetKaseyaPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetCommonPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetCommonPodDataByTicketId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetSyncroPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetSyncroPodDataByTicketId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetSuperOpsDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetServiceNowDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetZendeskPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetZendeskPodDataByTicketId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetSuperOpsDataByTicketId.fulfilled, (state, action) => action.payload);
    addCase(ThunkGetMicrosoftPodDataByContactId.fulfilled, (state, action) => action.payload);
    addCase(
      ThunkGetImportIntegrationPodDataByContactId.fulfilled,
      (state, action) => action.payload,
    );
    addCase(getFreshServiceDataByTicketId.fulfilled, (state, action) => action.payload);
    addCase(getFreshServiceDataByContactId.fulfilled, (state, action) => action.payload);

    addCase(ThunkUpdatePsaContact.fulfilled, (state, action) => {
      if (state.communicationItems?.length) {
        state.communicationItems = state.communicationItems.map(i =>
          i.Name === action.payload.type ? { ...i, Value: action.payload.value } : i,
        );
      }
    });

  },
});

export default ticketsSlice.reducer;

export const PodDataSelector = (state: RootState): IInitialState => state.pod;
export const { updateCommunicationItems } = ticketsSlice.actions;
